/** Server response shape for a blocked item delete (backend `ITEM_INVENTORY_DEPENDENCY`, NF-1B1-112). */
export const ITEM_DELETION_BLOCKED_CODE = 'ITEM_INVENTORY_DEPENDENCY';

export type ItemDependencyCategory = 'STOCK' | 'OPEN_DOCUMENT' | 'HISTORY';

export interface ItemDependency {
  key: string;
  label: string;
  category: ItemDependencyCategory;
  count: number;
  references: string[];
}

export interface ItemDeletionBlocked {
  message: string;
  itemCode?: string;
  recommendation: 'CLEAR_DEPENDENCY' | 'DEACTIVATE';
  dependencies: ItemDependency[];
}

export function parseItemDeletionBlocked(error: unknown): ItemDeletionBlocked | null {
  const response = (error as { response?: { status?: number; data?: any } })?.response;
  const body = response?.data;
  const details = body?.errors;
  if (response?.status !== 409 || details?.code !== ITEM_DELETION_BLOCKED_CODE) return null;
  return {
    message: String(body?.message ?? 'This item cannot be deleted.'),
    itemCode: details.item_code,
    recommendation: details.recommendation === 'DEACTIVATE' ? 'DEACTIVATE' : 'CLEAR_DEPENDENCY',
    dependencies: Array.isArray(details.dependencies) ? details.dependencies : [],
  };
}

const CATEGORY_TITLE: Record<ItemDependencyCategory, string> = {
  STOCK: 'Stock',
  OPEN_DOCUMENT: 'Open documents',
  HISTORY: 'Transaction history',
};

export function groupItemDependencies(dependencies: ItemDependency[]): { title: string; items: ItemDependency[] }[] {
  return (['STOCK', 'OPEN_DOCUMENT', 'HISTORY'] as ItemDependencyCategory[])
    .map((category) => ({ title: CATEGORY_TITLE[category], items: dependencies.filter((d) => d.category === category) }))
    .filter((group) => group.items.length > 0);
}

/** Message for any other delete failure; never shows raw server / database text. */
export function itemDeleteFailureMessage(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: any } })?.response;
  if (response?.status === 404) return 'This item no longer exists or belongs to another company.';
  if (response?.status === 403) return 'You do not have permission to delete items.';
  return 'The item could not be deleted. Please try again or contact support.';
}
